import React from 'react';
import { useTranslation } from 'react-i18next';

import PolicyNavigation from './PolicyNavigation';

interface CookiePolicyContentProps {
  onNavigate?: (view: string) => void;
}

const CookiePolicyContent: React.FC<CookiePolicyContentProps> = ({ onNavigate }) => {
  const { t } = useTranslation();
  return (
    <div className="max-w-4xl mx-auto vscode-text-primary pt-8">
      <h1 className="text-3xl font-bold mb-6 vscode-text-primary">{t('legal.cookieTitle')}</h1>
      
      <div className="prose max-w-none">
        <p className="text-sm vscode-text-secondary mb-6">
          <strong>{t('legal.cookie.effectiveDate')}:</strong> June 2025<br />
          <strong>{t('legal.cookie.lastUpdated')}:</strong> October 2025
        </p>

        <p className="text-lg vscode-text-secondary mb-6">
          {t('legal.cookie.intro')}{' '}
          <a href="/privacy-policy" className="text-blue-400 hover:text-blue-300">{t('legal.cookie.privacyLink')}</a>.
        </p>

        <div className="mb-8 p-4  ">
          <p className="vscode-text-secondary">{t('legal.cookie.consentIntro')}</p>
        </div>

        {/* Section 1 */}
        <h2 className="text-2xl font-semibold mt-8 mb-4">{t('legal.cookie.s1Title')}</h2>
        <p className="vscode-text-secondary mb-4">{t('legal.cookie.s1Intro')}</p>
        
        <h3 className="text-xl font-semibold mt-6 mb-3">{t('legal.cookie.s1_1Title')}</h3>
        <ul className="list-disc list-inside vscode-text-secondary mb-4 space-y-2 ml-4">
          <li>{t('legal.cookie.type1')}</li>
          <li>{t('legal.cookie.type2')}</li>
          <li>{t('legal.cookie.type3')}</li>
          <li>{t('legal.cookie.type4')}</li>
        </ul>

        {/* Section 2 */}
        <h2 className="text-2xl font-semibold mt-8 mb-4">{t('legal.cookie.s2Title')}</h2>
        <p className="vscode-text-secondary mb-4">{t('legal.cookie.s2Intro')}</p>

        <h3 className="text-xl font-semibold mt-6 mb-3">{t('legal.cookie.s2_1Title')}</h3>
        <p className="vscode-text-secondary mb-4">{t('legal.cookie.s2_1Intro')}</p>
        <div className=" p-4-lg mb-4">
          <table className="w-full text-sm vscode-text-secondary">
            <thead>
              <tr className="border-b vscode-border">
                <th className="text-left py-2">{t('legal.cookie.cookieName')}</th>
                <th className="text-left py-2">{t('legal.cookie.purpose')}</th>
                <th className="text-left py-2">{t('legal.cookie.duration')}</th>
              </tr>
            </thead>
            <tbody>
              <tr className="border-b vscode-border">
                <td className="py-2">{t('legal.cookie.sessionId')}</td>
                <td className="py-2">{t('legal.cookie.sessionPurpose')}</td>
                <td className="py-2">{t('legal.cookie.session')}</td>
              </tr>
              <tr className="border-b vscode-border">
                <td className="py-2">{t('legal.cookie.csrfToken')}</td>
                <td className="py-2">{t('legal.cookie.csrfPurpose')}</td>
                <td className="py-2">{t('legal.cookie.session')}</td>
              </tr>
              <tr className="border-b vscode-border">
                <td className="py-2">{t('legal.cookie.authToken')}</td>
                <td className="py-2">{t('legal.cookie.authPurpose')}</td>
                <td className="py-2">{t('legal.cookie.authDuration')}</td>
              </tr>
              <tr>
                <td className="py-2">{t('legal.cookie.cookieConsent')}</td>
                <td className="py-2">{t('legal.cookie.consentPurpose')}</td>
                <td className="py-2">{t('legal.cookie.oneYear')}</td>
              </tr>
            </tbody>
          </table>
        </div>
        <p className="text-sm vscode-text-secondary mb-4">{t('legal.cookie.noteEssential')}</p>

        <h3 className="text-xl font-semibold mt-6 mb-3">{t('legal.cookie.s2_2Title')}</h3>
        <p className="vscode-text-secondary mb-4">{t('legal.cookie.s2_2Intro')}</p>
        <div className=" p-4-lg mb-4">
          <table className="w-full text-sm vscode-text-secondary">
            <thead>
              <tr className="border-b vscode-border">
                <th className="text-left py-2">{t('legal.cookie.cookieName')}</th>
                <th className="text-left py-2">{t('legal.cookie.purpose')}</th>
                <th className="text-left py-2">{t('legal.cookie.duration')}</th>
              </tr>
            </thead>
            <tbody>
              <tr className="border-b vscode-border">
                <td className="py-2">{t('legal.cookie.languagePref')}</td>
                <td className="py-2">{t('legal.cookie.languagePurpose')}</td>
                <td className="py-2">{t('legal.cookie.oneYear')}</td>
              </tr>
              <tr className="border-b vscode-border">
                <td className="py-2">{t('legal.cookie.themePref')}</td>
                <td className="py-2">{t('legal.cookie.themePurpose')}</td>
                <td className="py-2">{t('legal.cookie.oneYear')}</td>
              </tr>
              <tr className="border-b vscode-border">
                <td className="py-2">{t('legal.cookie.dashboardLayout')}</td>
                <td className="py-2">{t('legal.cookie.dashboardPurpose')}</td>
                <td className="py-2">{t('legal.cookie.sixMonths')}</td>
              </tr>
              <tr>
                <td className="py-2">{t('legal.cookie.notificationPrefs')}</td>
                <td className="py-2">{t('legal.cookie.notificationPurpose')}</td>
                <td className="py-2">{t('legal.cookie.oneYear')}</td>
              </tr>
            </tbody>
          </table>
        </div>

        <h3 className="text-xl font-semibold mt-6 mb-3">{t('legal.cookie.s2_3Title')}</h3>
        <p className="vscode-text-secondary mb-4">{t('legal.cookie.s2_3Intro')}</p>
        <div className=" p-4-lg mb-4">
          <table className="w-full text-sm vscode-text-secondary">
            <thead>
              <tr className="border-b vscode-border">
                <th className="text-left py-2">{t('legal.cookie.service')}</th>
                <th className="text-left py-2">{t('legal.cookie.purpose')}</th>
                <th className="text-left py-2">{t('legal.cookie.duration')}</th>
              </tr>
            </thead>
            <tbody>
              <tr className="border-b vscode-border">
                <td className="py-2">{t('legal.cookie.googleAnalytics')}</td>
                <td className="py-2">{t('legal.cookie.gaPurpose')}</td>
                <td className="py-2">{t('legal.cookie.twoYears')}</td>
              </tr>
              <tr className="border-b vscode-border">
                <td className="py-2">{t('legal.cookie.firebaseAnalytics')}</td>
                <td className="py-2">{t('legal.cookie.firebasePurpose')}</td>
                <td className="py-2">{t('legal.cookie.twoYears')}</td>
              </tr>
              <tr>
                <td className="py-2">{t('legal.cookie.hotjar')}</td>
                <td className="py-2">{t('legal.cookie.hotjarPurpose')}</td>
                <td className="py-2">{t('legal.cookie.oneYear')}</td>
              </tr>
            </tbody>
          </table>
        </div>
        <p className="vscode-text-secondary mb-4">{t('legal.cookie.analyticsIncludes')}</p>
        <ul className="list-disc list-inside vscode-text-secondary mb-4 space-y-2 ml-4">
          <li>{t('legal.cookie.analytics1')}</li>
          <li>{t('legal.cookie.analytics2')}</li>
          <li>{t('legal.cookie.analytics3')}</li>
          <li>{t('legal.cookie.analytics4')}</li>
          <li>{t('legal.cookie.analytics5')}</li>
        </ul>

        <h3 className="text-xl font-semibold mt-6 mb-3">{t('legal.cookie.s2_4Title')}</h3>
        <p className="vscode-text-secondary mb-4">{t('legal.cookie.s2_4Intro')}</p>
        <div className=" p-4-lg mb-4">
          <table className="w-full text-sm vscode-text-secondary">
            <thead>
              <tr className="border-b vscode-border">
                <th className="text-left py-2">{t('legal.cookie.service')}</th>
                <th className="text-left py-2">{t('legal.cookie.purpose')}</th>
                <th className="text-left py-2">{t('legal.cookie.duration')}</th>
              </tr>
            </thead>
            <tbody>
              <tr className="border-b vscode-border">
                <td className="py-2">{t('legal.cookie.googleAds')}</td>
                <td className="py-2">{t('legal.cookie.googleAdsPurpose')}</td>
                <td className="py-2">{t('legal.cookie.ninetyDays')}</td>
              </tr>
              <tr className="border-b vscode-border">
                <td className="py-2">{t('legal.cookie.facebookPixel')}</td>
                <td className="py-2">{t('legal.cookie.facebookPurpose')}</td>
                <td className="py-2">{t('legal.cookie.ninetyDays')}</td>
              </tr>
              <tr>
                <td className="py-2">{t('legal.cookie.linkedInTag')}</td>
                <td className="py-2">{t('legal.cookie.linkedInPurpose')}</td>
                <td className="py-2">{t('legal.cookie.ninetyDays')}</td>
              </tr>
            </tbody>
          </table>
        </div>

        <h3 className="text-xl font-semibold mt-6 mb-3">{t('legal.cookie.s2_5Title')}</h3>
        <p className="vscode-text-secondary mb-4">{t('legal.cookie.s2_5Intro')}</p>
        <ul className="list-disc list-inside vscode-text-secondary mb-4 space-y-2 ml-4">
          <li>{t('legal.cookie.perf1')}</li>
          <li>{t('legal.cookie.perf2')}</li>
          <li>{t('legal.cookie.perf3')}</li>
          <li>{t('legal.cookie.perf4')}</li>
        </ul>

        {/* Section 3 */}
        <h2 className="text-2xl font-semibold mt-8 mb-4">{t('legal.cookie.s3Title')}</h2>
        <p className="vscode-text-secondary mb-4">{t('legal.cookie.s3Intro')}</p>

        <h3 className="text-xl font-semibold mt-6 mb-3">{t('legal.cookie.s3_1Title')}</h3>
        <ul className="list-disc list-inside vscode-text-secondary mb-4 space-y-2 ml-4">
          <li>{t('legal.cookie.auth1')}</li>
          <li>{t('legal.cookie.auth2')}</li>
          <li>{t('legal.cookie.auth3')}</li>
        </ul>

        <h3 className="text-xl font-semibold mt-6 mb-3">{t('legal.cookie.s3_2Title')}</h3>
        <ul className="list-disc list-inside vscode-text-secondary mb-4 space-y-2 ml-4">
          <li>{t('legal.cookie.pay1')}</li>
          <li>{t('legal.cookie.pay2')}</li>
        </ul>

        <h3 className="text-xl font-semibold mt-6 mb-3">{t('legal.cookie.s3_3Title')}</h3>
        <ul className="list-disc list-inside vscode-text-secondary mb-4 space-y-2 ml-4">
          <li>{t('legal.cookie.comm1')}</li>
          <li>{t('legal.cookie.comm2')}</li>
        </ul>

        <h3 className="text-xl font-semibold mt-6 mb-3">{t('legal.cookie.s3_4Title')}</h3>
        <ul className="list-disc list-inside vscode-text-secondary mb-4 space-y-2 ml-4">
          <li>{t('legal.cookie.cdn1')}</li>
          <li>{t('legal.cookie.cdn2')}</li>
        </ul>

        {/* Section 4 */}
        <h2 className="text-2xl font-semibold mt-8 mb-4">{t('legal.cookie.s4Title')}</h2>
        
        <h3 className="text-xl font-semibold mt-6 mb-3">{t('legal.cookie.s4_1Title')}</h3>
        <p className="vscode-text-secondary mb-4">{t('legal.cookie.s4_1Intro')}</p>
        <div className=" p-4-lg mb-4">
          <ul className="vscode-text-secondary space-y-3">
            <li>{t('legal.cookie.chrome')}<br /><span className="text-sm vscode-text-tertiary">chrome://settings/cookies</span></li>
            <li>{t('legal.cookie.firefox')}<br /><span className="text-sm vscode-text-tertiary">about:preferences#privacy</span></li>
            <li>{t('legal.cookie.safari')}</li>
            <li>{t('legal.cookie.edge')}<br /><span className="text-sm vscode-text-tertiary">edge://settings/privacy</span></li>
            <li>{t('legal.cookie.opera')}</li>
          </ul>
        </div>

        <h3 className="text-xl font-semibold mt-6 mb-3">{t('legal.cookie.s4_2Title')}</h3>
        <p className="vscode-text-secondary mb-4">{t('legal.cookie.s4_2Intro')}</p>
        <ul className="list-disc list-inside vscode-text-secondary mb-4 space-y-2 ml-4">
          <li>{t('legal.cookie.consent1')}</li>
          <li>{t('legal.cookie.consent2')}</li>
          <li>{t('legal.cookie.consent3')}</li>
        </ul>
        <p className="vscode-text-secondary mb-4">{t('legal.cookie.cookieSettingsLink')}</p>

        <h3 className="text-xl font-semibold mt-6 mb-3">{t('legal.cookie.s4_3Title')}</h3>
        <p className="vscode-text-secondary mb-4">{t('legal.cookie.s4_3Intro')}</p>
        <ul className="list-disc list-inside vscode-text-secondary mb-4 space-y-2 ml-4">
          <li><strong>{t('legal.cookie.googleAnalytics')}:</strong> <a href="https://tools.google.com/dlpage/gaoptout" className="text-blue-400 hover:text-blue-300" target="_blank" rel="noopener noreferrer">Google Analytics Opt-out Browser Add-on</a></li>
          <li><strong>{t('legal.cookie.googleAds')}:</strong> <a href="https://adssettings.google.com" className="text-blue-400 hover:text-blue-300" target="_blank" rel="noopener noreferrer">Google Ads Settings</a></li>
          <li><strong>{t('legal.cookie.facebookOptOutLabel')}</strong> <a href="https://www.facebook.com/ads/preferences" className="text-blue-400 hover:text-blue-300" target="_blank" rel="noopener noreferrer">{t('legal.cookie.facebookAdPrefs')}</a></li>
        </ul>

        <h3 className="text-xl font-semibold mt-6 mb-3">{t('legal.cookie.s4_4Title')}</h3>
        <p className="vscode-text-secondary mb-4">{t('legal.cookie.s4_4Intro')}</p>

        {/* Section 5 */}
        <h2 className="text-2xl font-semibold mt-8 mb-4">{t('legal.cookie.s5Title')}</h2>
        <p className="vscode-text-secondary mb-4">{t('legal.cookie.s5Intro')}</p>
        <div className="p-4 bg-yellow-900/20 border-l-4 border-yellow-500 mb-4">
          <ul className="list-disc list-inside vscode-text-secondary space-y-2 ml-4">
            <li>{t('legal.cookie.impact1')}</li>
            <li>{t('legal.cookie.impact2')}</li>
            <li>{t('legal.cookie.impact3')}</li>
            <li>{t('legal.cookie.impact4')}</li>
            <li>{t('legal.cookie.impact5')}</li>
            <li>{t('legal.cookie.impact6')}</li>
          </ul>
        </div>

        {/* Section 6 */}
        <h2 className="text-2xl font-semibold mt-8 mb-4">{t('legal.cookie.s6Title')}</h2>
        <p className="vscode-text-secondary mb-4">{t('legal.cookie.s6Intro')}</p>

        <h3 className="text-xl font-semibold mt-6 mb-3">{t('legal.cookie.s6_1Title')}</h3>
        <ul className="list-disc list-inside vscode-text-secondary mb-4 space-y-2 ml-4">
          <li>{t('legal.cookie.iosId')}</li>
          <li>{t('legal.cookie.androidId')}</li>
        </ul>

        <h3 className="text-xl font-semibold mt-6 mb-3">{t('legal.cookie.s6_2Title')}</h3>
        <ul className="list-disc list-inside vscode-text-secondary mb-4 space-y-2 ml-4">
          <li>{t('legal.cookie.iosManage')}</li>
          <li>{t('legal.cookie.androidManage')}</li>
        </ul>

        {/* Section 7 */}
        <h2 className="text-2xl font-semibold mt-8 mb-4">{t('legal.cookie.s7Title')}</h2>
        
        <h3 className="text-xl font-semibold mt-6 mb-3">{t('legal.cookie.s7_1Title')}</h3>
        <p className="vscode-text-secondary mb-4">{t('legal.cookie.s7_1Intro')}</p>
        <ul className="list-disc list-inside vscode-text-secondary mb-4 space-y-2 ml-4">
          <li>{t('legal.cookie.gdpr1')}</li>
          <li>{t('legal.cookie.gdpr2')}</li>
          <li>{t('legal.cookie.gdpr3')}</li>
          <li>{t('legal.cookie.gdpr4')}</li>
        </ul>

        <h3 className="text-xl font-semibold mt-6 mb-3">{t('legal.cookie.s7_2Title')}</h3>
        <p className="vscode-text-secondary mb-4">{t('legal.cookie.s7_2Intro')}</p>
        <ul className="list-disc list-inside vscode-text-secondary mb-4 space-y-2 ml-4">
          <li>{t('legal.cookie.ccpa1')}</li>
          <li>{t('legal.cookie.ccpa2')}</li>
          <li>{t('legal.cookie.ccpa3')}</li>
        </ul>

        <h3 className="text-xl font-semibold mt-6 mb-3">{t('legal.cookie.s7_3Title')}</h3>
        <p className="vscode-text-secondary mb-4">{t('legal.cookie.s7_3Intro')}</p>
        <ul className="list-disc list-inside vscode-text-secondary mb-4 space-y-2 ml-4">
          <li>{t('legal.cookie.eprivacy1')}</li>
          <li>{t('legal.cookie.eprivacy2')}</li>
          <li>{t('legal.cookie.eprivacy3')}</li>
          <li>{t('legal.cookie.eprivacy4')}</li>
        </ul>

        {/* Section 8 */}
        <h2 className="text-2xl font-semibold mt-8 mb-4">{t('legal.cookie.s8Title')}</h2>
        <p className="vscode-text-secondary mb-4">{t('legal.cookie.s8Intro')}</p>
        <ul className="list-disc list-inside vscode-text-secondary mb-4 space-y-2 ml-4">
          <li>{t('legal.cookie.updates1')}</li>
          <li>{t('legal.cookie.updates2')}</li>
          <li>{t('legal.cookie.updates3')}</li>
        </ul>

        {/* Section 9 */}
        <h2 className="text-2xl font-semibold mt-8 mb-4">{t('legal.cookie.s9Title')}</h2>
        <p className="vscode-text-secondary mb-4">{t('legal.cookie.s9Intro')}</p>
        <div className=" p-6-lg mb-4">
          <p className="vscode-text-secondary mb-2"><strong>{t('legal.cookie.contactTitle')}</strong></p>
          <p className="vscode-text-secondary mb-2">
            {t('legal.cookie.contactEmail')} <a href="mailto:support@gograbjob.com" className="text-blue-400 hover:text-blue-300">support@gograbjob.com</a>
          </p>
          <p className="vscode-text-secondary mb-2">
            {t('legal.cookie.cookieQuestions')} <a href="mailto:support@gograbjob.com" className="text-blue-400 hover:text-blue-300">support@gograbjob.com</a>
          </p>
          <p className="vscode-text-secondary">
            {t('legal.cookie.contactWebsite')} <a href="https://www.gograbjob.com" className="text-blue-400 hover:text-blue-300">www.gograbjob.com</a>
          </p>
        </div>

        {/* Summary Box */}
        <div className="mt-8 p-6  ">
          <p className="vscode-text-secondary"><strong>{t('legal.cookie.quickSummary')}</strong></p>
        </div>

        {/* Related Links */}
        <div className="mt-8 p-4 -lg">
          <p className="text-sm vscode-text-secondary mb-2"><strong>{t('legal.cookie.relatedTitle')}</strong></p>
          <ul className="text-sm vscode-text-secondary space-y-1">
            <li>• <a href="/privacy-policy" className="text-blue-400 hover:text-blue-300">{t('legal.cookie.privacyLink')}</a></li>
            <li>• <a href="/terms-of-service" className="text-blue-400 hover:text-blue-300">{t('legal.cookie.termsLink')}</a></li>
            <li>• <a href="/gdpr-compliance" className="text-blue-400 hover:text-blue-300">{t('legal.cookie.gdprLink')}</a></li>
          </ul>
        </div>
      </div>

      {/* Related Policies Navigation */}
      <PolicyNavigation currentPolicy="cookie" onNavigate={onNavigate} />
    </div>
  );
};

export default CookiePolicyContent;
